import React, { useState } from 'react';
import { Check, Star, ArrowRight, User, Briefcase, Users, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = () => {
  const [activeTab, setActiveTab] = useState('particuliers');

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const categories = {
    particuliers: {
      icon: User,
      title: 'Particuliers',
      packs: [
        {
          name: 'Essentiel',
          price: '70 €',
          description: 'Intervention ciblée',
          tagline: 'Une action précise, un résultat immédiat.',
          features: [
            'Rédaction assistée IA (courrier CAF, bailleur, assurance)',
            'Analyse et réponse à un document reçu',
            'Réclamation ou contestation structurée',
            'Formulaire complété avec vérification'
          ]
        },
        {
          name: 'Premium',
          price: '220 €',
          description: 'Diagnostic IA + Accompagnement administratif',
          tagline: 'Analyse complète. Structuration. Résolution.',
          features: [
            'Diagnostic IA de votre situation',
            'Traitement multi-démarches (CAF, CPAM, impôts)',
            'Suivi et relances automatisées',
            'Litige organisme : stratégie + exécution'
          ]
        },
        {
          name: 'Excellence',
          price: '450 €',
          description: 'Structuration IA + Régularisation complète',
          tagline: 'Reprise en main totale assistée par IA.',
          popular: true,
          features: [
            'Audit administratif complet',
            'Régularisation multi-organismes',
            'Structuration dossier avec outils IA',
            'Plan d\'action personnalisé'
          ]
        },
        {
          name: 'Excellence+',
          price: '650 €',
          description: 'Conformité + Automatisation avancée',
          tagline: 'Dossiers complexes. Prise en charge intégrale.',
          features: [
            'Dossier retraite / surendettement / logement',
            'Gestion multi-organismes avec historique',
            'Workflows automatisés pour suivi',
            'Reporting et tableaux de bord'
          ]
        },
        {
          name: 'Excellence Premium',
          price: '900 à 1 200 €',
          description: 'Direction administrative & IA externalisée',
          tagline: 'Votre consultante dédiée. Pilotage complet.',
          features: [
            'Gestion administrative totale',
            'Outils IA sur mesure déployés',
            'Automatisations personnalisées',
            'Accompagnement stratégique continu'
          ]
        }
      ]
    },
    entrepreneurs: {
      icon: Briefcase,
      title: 'Entrepreneurs / Indépendants',
      packs: [
        {
          name: 'Essentiel',
          price: '90 €',
          description: 'Intervention ciblée',
          features: [
            'Rédaction professionnelle assistée IA',
            'Réponse structurée URSSAF / impôts',
            'Mise en forme officielle',
            'Analyse document administratif'
          ]
        },
        {
          name: 'Premium',
          price: '180 €',
          description: 'Diagnostic IA + Accompagnement administratif',
          features: [
            'Diagnostic IA de votre situation pro',
            'Dossier URSSAF structuré',
            'Mise en conformité assistée',
            'Suivi automatisé des échéances'
          ]
        },
        {
          name: 'Excellence',
          price: '350 €',
          description: 'Structuration IA + Régularisation',
          popular: true,
          features: [
            'Audit administratif complet',
            'Régularisation multi-organismes',
            'Création système d\'organisation IA',
            'Dossier bancaire / assurance optimisé'
          ]
        },
        {
          name: 'Excellence+',
          price: '550 €',
          description: 'Conformité + Automatisation avancée',
          features: [
            'Reprise historique administratif',
            'Conformité URSSAF + impôts + assurance',
            'Workflows automatisés déployés',
            'Tableaux de bord personnalisés'
          ]
        },
        {
          name: 'Accompagnement Mensuel',
          price: '450 à 650 €',
          description: 'Direction administrative & IA externalisée',
          features: [
            'Pilotage administratif externalisé',
            'Automatisations et relances IA',
            'Interface organismes optimisée',
            'Reporting mensuel'
          ]
        }
      ]
    },
    associations: {
      icon: Users,
      title: 'Associations',
      packs: [
        {
          name: 'Essentiel',
          price: '80 €',
          description: 'Intervention ciblée',
          features: [
            'Rédaction officielle assistée IA',
            'Mise à jour administrative',
            'Analyse et correction documents'
          ]
        },
        {
          name: 'Premium',
          price: '160 €',
          description: 'Diagnostic IA + Accompagnement statutaire',
          features: [
            'Diagnostic IA de votre structure',
            'Modification statuts optimisée',
            'Déclaration préfecture structurée',
            'Conformité documentaire'
          ]
        },
        {
          name: 'Excellence',
          price: '300 €',
          description: 'Structuration IA + Création / Subventions',
          popular: true,
          features: [
            'Création association complète',
            'Dossier subvention optimisé IA',
            'Régularisation administrative',
            'Outils de gestion déployés'
          ]
        },
        {
          name: 'Excellence+',
          price: '500 €',
          description: 'Conformité + Automatisation avancée',
          features: [
            'Dossier subvention multi-financeurs',
            'Restructuration administrative complète',
            'Workflows automatisés',
            'Tableaux de suivi personnalisés'
          ]
        },
        {
          name: 'Accompagnement Mensuel',
          price: '350 à 550 €',
          description: 'Direction administrative & IA externalisée',
          features: [
            'Pilotage administratif externalisé',
            'Interface mairie / préfecture / financeurs',
            'Automatisations et relances',
            'Reporting mensuel'
          ]
        }
      ]
    },
    entreprises: {
      icon: Building2,
      title: 'Entreprises',
      packs: [
        {
          name: 'Essentiel',
          price: '120 €',
          description: 'Intervention ciblée',
          features: [
            'Rédaction professionnelle assistée IA',
            'Mise en forme officielle',
            'Analyse et reformulation stratégique'
          ]
        },
        {
          name: 'Premium',
          price: '280 €',
          description: 'Diagnostic IA + Accompagnement administratif',
          features: [
            'Diagnostic IA de votre situation',
            'Gestion litige structurée',
            'Conformité documentaire',
            'Suivi automatisé des actions'
          ]
        },
        {
          name: 'Excellence',
          price: '450 €',
          description: 'Structuration IA + Organisation complète',
          popular: true,
          features: [
            'Audit administratif approfondi',
            'Système d\'organisation IA déployé',
            'Régularisation dossiers complexes',
            'Processus optimisés'
          ]
        },
        {
          name: 'Excellence+',
          price: '650 €',
          description: 'Conformité + Automatisation avancée',
          features: [
            'Conformité complète multi-organismes',
            'Reprise historique administratif',
            'Workflows automatisés sur mesure',
            'Tableaux de bord et reporting'
          ]
        },
        {
          name: 'Direction Administrative',
          price: '900 à 1 200 €/mois',
          description: 'Direction administrative & IA externalisée',
          features: [
            'Pilotage administratif complet',
            'Outils IA sur mesure déployés',
            'Interface organismes / fournisseurs / clients',
            'Reporting stratégique mensuel'
          ]
        }
      ]
    }
  };

  const tabs = [
    { id: 'entreprises', label: 'Entreprises', icon: Building2 },
    { id: 'entrepreneurs', label: 'Entrepreneurs', icon: Briefcase },
    { id: 'associations', label: 'Associations', icon: Users },
    { id: 'particuliers', label: 'Particuliers', icon: User }
  ];

  const currentCategory = categories[activeTab];

  return (
    <section id="pricing" className="py-32 bg-black relative" data-testid="pricing-section">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A55C]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-6">
          <span className="text-sm font-semibold tracking-[0.3em] uppercase text-[#C9A55C]">
            Tarifs
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white">
            Nos formules sur mesure
          </h2>
          <p className="text-lg text-white/60">
            Forfaits clairs. Tarification transparente. Résultats garantis.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-16">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#C9A55C] text-black'
                  : 'border border-[#C9A55C]/30 text-white/70 hover:border-[#C9A55C] hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Category Title */}
        <div className="text-center mb-12">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#C9A55C]">
            {currentCategory.title}
          </h3>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {currentCategory.packs.map((pack, index) => (
            <div
              key={index}
              className={`relative p-6 transition-all duration-500 flex flex-col ${
                pack.popular
                  ? 'bg-gradient-to-b from-[#C9A55C]/20 to-transparent border-2 border-[#C9A55C]'
                  : 'border border-[#C9A55C]/20 hover:border-[#C9A55C]/50'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 bg-[#C9A55C] text-black text-xs font-bold px-3 py-1 tracking-wider uppercase">
                    <Star className="w-3 h-3 fill-current" />
                    Populaire
                  </span>
                </div>
              )}

              <div className="space-y-4 flex-grow">
                <div className="text-center">
                  <h4 className="text-lg font-serif font-bold text-white">{pack.name}</h4>
                  <p className="text-xs text-white/50 mt-1">{pack.description}</p>
                </div>

                <div className="text-center py-4 border-y border-[#C9A55C]/20">
                  <p className="text-xl font-bold text-[#C9A55C]">{pack.price}</p>
                </div>

                <ul className="space-y-2">
                  {pack.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C9A55C] flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>

                {pack.tagline && (
                  <p className="text-xs text-[#C9A55C] italic text-center pt-2 border-t border-[#C9A55C]/10">
                    {pack.tagline}
                  </p>
                )}
              </div>

              <Button
                onClick={scrollToContact}
                className={`w-full rounded-none h-10 font-semibold tracking-wider uppercase text-xs transition-all mt-4 ${
                  pack.popular
                    ? 'btn-gold'
                    : 'bg-transparent border border-[#C9A55C]/50 text-[#C9A55C] hover:bg-[#C9A55C] hover:text-black'
                }`}
              >
                Choisir
                <ArrowRight className="ml-2 w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>

        {/* Additional info */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap justify-center gap-8 text-sm text-white/40">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A55C]" />
              Premier échange gratuit
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A55C]" />
              Devis sans engagement
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#C9A55C]" />
              Modalités financières définies selon l'exigence du projet
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
